import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CircleCheck,
  CirclePause,
  Download,
  Layers,
  Megaphone,
  Pencil,
  Plus,
  Settings2, Users } from 'lucide-react'
import Button from '../../components/Button'
import { useRemembered } from '../../components/listMemory'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import FilterBar from '../../components/FilterBar'
import IconButton from '../../components/IconButton'
import AwarenessViewsDrawer from './AwarenessViewsDrawer'
import PageHeader from '../../components/PageHeader'
import FeatureSwitch from '../../components/FeatureSwitch'
import useFeatureToggle from '../../components/useFeatureToggle'
import SectionNav from '../../components/SectionNav'
import StatStrip from '../../components/StatStrip'
import useHashTab from '../../components/useHashTab'
import { useToast } from '../../components/toastContext'
import { dateColumn, statusColumn } from './cells'
import AwarenessSettingsPanel from './AwarenessSettingsPanel'
import {
  getAwarenessPage,
  exportAwareness,
  listAwareness,
  saveAwareness,
  saveAwarenessScreenSettings,
} from './engagementService'
import { engagementStats as ST, awarenessStrings as S } from './strings'
import './engagement.css'
import { serverText } from '../../lib/i18n'

const T = S.settings

/* Two sections, because the screen's own settings are not a bulletin — and stacking a
   settings card under the list would make one long scroll of two unrelated things. */
const SECTIONS = ['content', 'settings']

/** النشرات التوعوية — Views/Awareness/Index.cshtml. */
export default function AwarenessPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const [query, setQuery] = useRemembered('awareness:query', '')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [pendingToggle, setPendingToggle] = useState(null)
  const [viewing, setViewing] = useState(null)

  /* Remembered like the search above — the scope card decides WHICH bulletins those
     page numbers count, and Back used to return to page 2 of the unfiltered list. */
  const [scope, setScope] = useRemembered('awareness:scope', null)

  const [reloadKey, setReloadKey] = useState(0)
  const [tab, setTab] = useHashTab('content', SECTIONS)
  /* The screen's own settings — on the payload since the slice was wired, read by
     nothing. `null` until it answers, so the panel never opens on defaults. */
  const [settings, setSettings] = useState(null)

  /* Shared with every other activation switch — see useFeatureToggle. The
     endpoint writes all three screen settings at once, so the two names are sent
     back unchanged; sending them empty would rename the screen to nothing. */
  const { toggle: toggleFeature } = useFeatureToggle({
    setValue: (enabled) => setSettings((previous) => ({ ...previous, enabled })),
    save: (enabled) =>
      saveAwarenessScreenSettings({
        enabled,
        screenNameAr: settings?.screenNameAr ?? '',
        screenNameEn: settings?.screenNameEn ?? '',
      }),
  })

  /* The page payload carries the screen's settings and nothing else this list needs, so it
     is read once rather than on every reload of the rows. An empty effect was sitting here
     with nothing in it. */
  useEffect(() => {
    getAwarenessPage().then((page) => setSettings(page.settings))
  }, [])

  useEffect(() => {
    setLoading(true)
    listAwareness()
      .then((response) => {
        setRows(response.rows)
        setLoading(false)
      })
      .catch((error) => {
        setRows([])
        setLoading(false)
        toast.error(error?.message || S.actionFailed)
      })
  }, [reloadKey, toast])

  /* Counted off the rows already on the page; the two state cards narrow the
     list to what they count. */
  const stats = useMemo(() => {
    const active = rows.filter((row) => row.isActive).length
    return { total: rows.length, active, inactive: rows.length - active }
  }, [rows])

  const visibleRows = useMemo(() => {
    if (scope === 'active') return rows.filter((row) => row.isActive)
    if (scope === 'inactive') return rows.filter((row) => !row.isActive)
    return rows
  }, [rows, scope])

  const columns = useMemo(
    () => [
      { key: 'title', header: S.columns.title, render: (row) => <bdi>{row.title}</bdi> },
      { key: 'user', header: S.columns.user, render: (row) => <bdi>{row.user}</bdi> },
      dateColumn('createdOn', S.columns.createdOn),
      statusColumn('isActive', S.columns.isActive, S),
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '96px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <span className="data-table__row-actions">
            <IconButton icon={Pencil} label={S.row.edit} onClick={() => navigate(`/awareness/${row.id}/edit`)} />
            {/* NOT a delete. Neither the API nor the legacy can remove a bulletin — the
                legacy's only delete on this screen is for an attachment — so the action
                says what it does: it takes the bulletin off the learner's list, and puts
                it back.

                IT USED TO SAY IT BADLY. A crossed-out eye IN RED is the picture of two
                other things — a preview turned off, and a delete — and it was neither;
                the red in particular is this app's mark for what cannot be undone, which
                taking a bulletin down very much can. The paused circle is what the status
                chip beside it and every other list in the panel already use for the same
                decision. */}
            <IconButton
              icon={row.isActive ? CirclePause : CircleCheck}
              label={row.isActive ? S.row.deactivate : S.row.activate}
              tone={row.isActive ? 'warning' : 'success'}
              onClick={() => setPendingToggle(row)}
            />
            {/* WHO READ IT. `.../views` has always answered this and nothing asked. */}
            <IconButton icon={Users} label={S.row.views} tone="violet" onClick={() => setViewing(row)} />
            <IconButton
              icon={Download}
              label={S.row.exportPackage}
              onClick={() => exportAwareness(row.id).catch((error) => toast.error(error?.message || S.actionFailed))}
            />
          </span>
        ),
      },
    ],
    [navigate, toast],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        subtitle={S.subtitle}
        count={tab === 'content' && !loading ? rows.length : undefined}
        actions={
          <>
            {/* Activation lives in the header, as it does on surveys,
                assessments, policies and security reports. It used to be a field
                inside the settings tab, so whether the awareness screen was on
                at all could only be seen — or changed — after switching tabs. */}
            {settings && (
              <FeatureSwitch
                checked={settings.enabled}
                onChange={toggleFeature}
                label={T.enabled}
              />
            )}
            {tab === 'content' && (
              <Button onClick={() => navigate('/awareness/new')}>
                <Plus size={16} strokeWidth={2} />
                {S.create}
              </Button>
            )}
          </>
        }
      />

      <div className="eng__workspace">
        <SectionNav
          groups={[
            {
              items: [
                { id: 'content', label: T.contentTab, icon: Megaphone },
                { id: 'settings', label: T.tab, icon: Settings2 },
              ],
            },
          ]}
          value={tab}
          onChange={setTab}
          ariaLabel={S.title}
        />

        <div className="eng__panel">
          {tab === 'content' && (
            <>

      <StatStrip
        label={ST.caption}
        activeId={scope ?? 'all'}
        onSelect={(id) => {
          /* ALL IS NOT A FILTER TO TOGGLE, it is the absence of one, so it clears the scope
             outright rather than flipping it back on when clicked twice. */
          if (id === 'all') return setScope(null)
          setScope((current) => (current === id ? null : id))
        }}
        items={[
          {
            /* Was an unclickable figure labelled "Total": the only way back to the full
               list was to click the selected card a second time. */
            id: 'all',
            icon: Layers,
            label: S.allContent,
            value: stats.total,
            tone: 'violet',
          },
          {
            id: 'active',
            icon: CircleCheck,
            label: ST.active,
            value: stats.active,
            tone: 'success',
          },
          {
            id: 'inactive',
            icon: CirclePause,
            label: ST.inactive,
            value: stats.inactive,
            tone: 'warning',
          },
        ]}
      />

      <FilterBar search={{ value: query, onChange: setQuery, placeholder: S.columns.title }} />

      <DataTable
        stateKey="awareness"
        columns={columns}
        rows={visibleRows}
        loading={loading}
        query={query}
        /* The scope card sits outside the table, which cannot see it — narrowing to the
           inactive bulletins kept a page the shorter list does not have. */
        resetKey={scope}
        controls={false}
        getRowId={(row) => row.id}
        initialSort={{ key: 'title', dir: 'asc' }}
      />
            </>
          )}

          {/* Only once the payload has answered — a settings form that opens on defaults
              and is then saved writes those defaults over what was there. */}
          {tab === 'settings' && settings && (
            <AwarenessSettingsPanel
              settings={settings}
              onChange={(patch) => setSettings((previous) => ({ ...previous, ...patch }))}
            />
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!pendingToggle}
        title={pendingToggle?.isActive ? S.deactivateTitle : S.activateTitle}
        tone={pendingToggle?.isActive ? 'warning' : 'success'}
        message={pendingToggle?.isActive ? S.deactivateConfirm : S.activateConfirm}
        confirmLabel={pendingToggle?.isActive ? S.row.deactivate : S.row.activate}
        cancelLabel={S.close}
        /* A save with `IsActive` flipped — the only write the API offers for this. The
           SAVE carries the whole bulletin, so the row's own Arabic and English titles and
           bodies go back untouched rather than the display versions of them. */
        onConfirm={async () => {
          const outcome = await saveAwareness({
            ...pendingToggle,
            isActive: !pendingToggle.isActive,
          })
          if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
          const activated = !pendingToggle.isActive
          setPendingToggle(null)
          /* `S.deleted` — "the operation completed successfully" — was the fallback here,
             which said nothing about what happened and read as a delete's confirmation.
             The panel now says which of the two things it did. */
          toast.success(serverText(outcome.message) || (activated ? S.activated : S.deactivated))
          setReloadKey((key) => key + 1)
        }}
        onCancel={() => setPendingToggle(null)}
      />

      <AwarenessViewsDrawer
        open={!!viewing}
        awareness={viewing}
        onClose={() => setViewing(null)}
      />
    </>
  )
}
